import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, Thermometer, Bluetooth, Camera, FileText, Zap, Shield, Globe } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ERPConfigPanel } from '../components/integration/ERPConfigPanel';
import { TemperatureMonitor } from '../components/temperature/TemperatureMonitor';
import { BluetoothScannerSetup } from '../components/scanner/BluetoothScannerSetup';
import { AdvancedReporting } from '../components/advanced/AdvancedReporting';
import { ERPIntegrationService } from '../services/erpIntegration';
import { useAppStore } from '../stores/appStore';

type FeatureTab = 'erp' | 'temperature' | 'bluetooth' | 'photo' | 'reporting' | 'security' | 'i18n';

export const AdvancedFeaturesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FeatureTab>('erp');
  const { addNotification } = useAppStore();

  const features = [
    {
      id: 'erp' as FeatureTab,
      name: 'ERP Integration',
      description: 'Connect to major ERP/WMS systems',
      icon: <Settings className="w-5 h-5" />,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'temperature' as FeatureTab,
      name: 'Temperature Monitoring',
      description: 'Cold chain compliance tracking',
      icon: <Thermometer className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'bluetooth' as FeatureTab,
      name: 'Bluetooth Scanners',
      description: 'Hardware scanner integration',
      icon: <Bluetooth className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 'photo' as FeatureTab,
      name: 'Photo Documentation',
      description: 'Visual verification and damage reporting',
      icon: <Camera className="w-5 h-5" />,
      color: 'bg-orange-100 text-orange-600'
    },
    {
      id: 'reporting' as FeatureTab,
      name: 'Advanced Reporting',
      description: 'Comprehensive analytics and exports',
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-indigo-100 text-indigo-600'
    },
    {
      id: 'security' as FeatureTab,
      name: 'Security Features',
      description: 'Enhanced authentication and audit trails',
      icon: <Shield className="w-5 h-5" />,
      color: 'bg-red-100 text-red-600'
    },
    {
      id: 'i18n' as FeatureTab,
      name: 'Internationalization',
      description: 'Multi-language support',
      icon: <Globe className="w-5 h-5" />,
      color: 'bg-yellow-100 text-yellow-600'
    }
  ];

  const handleERPConfigSave = async (config: any) => {
    try {
      // Save ERP configuration
      addNotification({
        type: 'success',
        title: 'Configuration Saved',
        message: 'ERP integration settings have been saved',
        priority: 'medium'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Save Failed',
        message: 'Failed to save ERP configuration',
        priority: 'high'
      });
    }
  };

  const handleERPTestConnection = async (config: any): Promise<boolean> => {
    try {
      const service = new ERPIntegrationService(config);
      return await service.testConnection();
    } catch (error) {
      return false;
    }
  };

  const handleERPSync = async (config: any) => {
    try {
      const service = new ERPIntegrationService(config);
      await service.syncOrders();
      
      addNotification({
        type: 'success',
        title: 'Sync Complete',
        message: 'ERP data synchronization completed successfully',
        priority: 'medium'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Sync Failed',
        message: 'ERP synchronization failed',
        priority: 'high'
      });
    }
  };

  const handleScanResult = (barcode: string) => {
    addNotification({
      type: 'success',
      title: 'Scan Successful',
      message: `Scanned: ${barcode}`,
      priority: 'low'
    });
  };

  const renderFeatureContent = () => {
    switch (activeTab) {
      case 'erp':
        return (
          <ERPConfigPanel
            onConfigSave={handleERPConfigSave}
            onTestConnection={handleERPTestConnection}
            onSync={handleERPSync}
          />
        );
      
      case 'temperature':
        return <TemperatureMonitor />;
      
      case 'bluetooth':
        return <BluetoothScannerSetup onScanResult={handleScanResult} />;
      
      case 'photo':
        return (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Photo Documentation</h3>
              <p className="text-gray-600 mb-4">
                Capture and manage photos for verification, damage reporting, and quality control.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Pick Verification</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Take photos to verify picked items and quantities
                  </p>
                  <Button variant="outline" size="sm">
                    <Camera className="w-4 h-4 mr-2" />
                    Capture Photo
                  </Button>
                </div>
                
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Damage Reporting</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Document damaged items with photos and annotations
                  </p>
                  <Button variant="outline" size="sm">
                    <Camera className="w-4 h-4 mr-2" />
                    Report Damage
                  </Button>
                </div>
                
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Quality Control</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Visual quality checks with photo evidence
                  </p>
                  <Button variant="outline" size="sm">
                    <Camera className="w-4 h-4 mr-2" />
                    Quality Check
                  </Button>
                </div>
                
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Delivery Proof</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Capture delivery confirmation photos
                  </p>
                  <Button variant="outline" size="sm">
                    <Camera className="w-4 h-4 mr-2" />
                    Delivery Photo
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );
      
      case 'reporting':
        return <AdvancedReporting />;
      
      case 'security':
        return (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Features</h3>
              <p className="text-gray-600 mb-6">
                Advanced security features including enhanced authentication, audit trails, and access control.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Authentication</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Multi-factor Authentication</span>
                      <span className="text-green-600">✓ Enabled</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Session Timeout</span>
                      <span className="text-gray-600">8 hours</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Password Policy</span>
                      <span className="text-green-600">✓ Strong</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Audit & Compliance</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Audit Logging</span>
                      <span className="text-green-600">✓ Active</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Data Retention</span>
                      <span className="text-gray-600">365 days</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Compliance Reports</span>
                      <span className="text-green-600">✓ Available</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex space-x-3">
                  <Button variant="outline">
                    View Audit Logs
                  </Button>
                  <Button variant="outline">
                    Security Settings
                  </Button>
                  <Button variant="outline">
                    Generate Report
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );
      
      case 'i18n':
        return (
          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Internationalization</h3>
              <p className="text-gray-600 mb-6">
                Multi-language support with localized date, time, and number formats.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Supported Languages</h4>
                  <div className="space-y-2">
                    {[
                      { code: 'en', name: 'English', flag: '🇺🇸' },
                      { code: 'es', name: 'Spanish', flag: '🇪🇸' },
                      { code: 'fr', name: 'French', flag: '🇫🇷' },
                      { code: 'de', name: 'German', flag: '🇩🇪' },
                      { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
                      { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
                      { code: 'ar', name: 'Arabic', flag: '🇸🇦' }
                    ].map(lang => (
                      <div key={lang.code} className="flex items-center space-x-3 p-2 border border-gray-200 rounded">
                        <span className="text-xl">{lang.flag}</span>
                        <span className="font-medium">{lang.name}</span>
                        <span className="text-sm text-gray-500">({lang.code})</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Localization Features</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Date Formats</span>
                      <span className="text-green-600">✓ Localized</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Time Formats</span>
                      <span className="text-green-600">✓ Localized</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Number Formats</span>
                      <span className="text-green-600">✓ Localized</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Currency Formats</span>
                      <span className="text-green-600">✓ Localized</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>RTL Support</span>
                      <span className="text-green-600">✓ Available</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex space-x-3">
                  <Button variant="outline">
                    Language Settings
                  </Button>
                  <Button variant="outline">
                    Add Translation
                  </Button>
                  <Button variant="outline">
                    Export Translations
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="p-4 sm:p-6 pb-20 sm:pb-24 max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Advanced Features
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Enterprise-grade functionality and integrations
            </p>
          </div>
        </div>
      </motion.div>

      {/* Feature Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
          {features.map((feature) => (
            <button
              key={feature.id}
              onClick={() => setActiveTab(feature.id)}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap
                ${activeTab === feature.id
                  ? 'bg-white text-blue-800 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <div className={`w-6 h-6 rounded flex items-center justify-center ${
                activeTab === feature.id ? feature.color : 'text-gray-400'
              }`}>
                {feature.icon}
              </div>
              <span className="hidden sm:inline">{feature.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Feature Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {renderFeatureContent()}
      </motion.div>
    </div>
  );
};